import { Dispatch, FunctionComponent, RefObject, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { faCoin } from "@fortawesome/pro-regular-svg-icons";

import { SelectListPopover } from "@/pages/Private/pages/Contacts/components/SelectListPopover";

import { Icon } from "../Icon/Icon";
import { Button } from "../Button/Button";
import { ButtonColor, ButtonSize } from "../Button/types";
import { Toggle } from "../Toggle/Toggle";
import { Information } from "../Information/Information";
import { Tabs } from "./tabs";
import { ByUrlFooterStatus } from "./ByUrlTab";

interface GlobalSearchFooterProps {
	currentTab: Tabs;
	totalSelected: number;
	selectedCompany: { name?: string | null } | null | undefined;
	foundEntities: {
		totalFound: number;
		contactsItems: number;
		organizationsFound: number;
		totalFoundForFilters: number;
	};
	isEnrichAllEmails: boolean;
	setIsEnrichAllEmails: Dispatch<SetStateAction<boolean>>;
	isEnrichAllPhones: boolean;
	setIsEnrichAllPhones: Dispatch<SetStateAction<boolean>>;
	estimatedCredits: { total: number; email: number; phone: number; linkedin: number };
	onClearSelection: () => void;
	popoverRef: RefObject<HTMLDivElement>;
	// True when the "Add to list" picker popover should be shown (multi-select, no targetList).
	showAddToListPopover: boolean;
	onToggleAddToListPopover: () => void;
	onAddToList: (list: { id?: number; name: string }) => void;
	// "Add to contact": shown beside "Add to list" in the default list-picker mode. Adds the
	// selection to Contacts (no list assignment).
	showAddToContacts: boolean;
	onAddToContacts: () => void;
	isAddingToContacts: boolean;
	targetList?: { id: number; name: string };
	singleSelect: boolean;
	isBulkAdding: boolean;
	isWriting: boolean;
	onWriteMessage: () => void;
	urlFooterStatus: ByUrlFooterStatus;
	// Confirm-selection mode: the primary action hands the resolved selection to the caller
	// instead of adding to a list — takes precedence over "Write message" / "Add to list".
	confirmMode: boolean;
	confirmLabel?: string;
	isConfirming: boolean;
	onConfirmSelection: () => void;
	// Wrong-person referral flow: already-translated note explaining the prefilled filters,
	// shown on the filters tab until a search returns results.
	footerNote?: string;
}

// Left side of the GlobalSearchModal footer: result-count summaries, selection chip + enrich
// toggles + credit estimate, and the primary action ("Add to list" / "Write message"). Extracted
// from GlobalSearchModal to keep that component under the file-size limit and focused on search UI.
export const GlobalSearchFooter: FunctionComponent<GlobalSearchFooterProps> = ({
	currentTab,
	totalSelected,
	selectedCompany,
	foundEntities,
	isEnrichAllEmails,
	setIsEnrichAllEmails,
	isEnrichAllPhones,
	setIsEnrichAllPhones,
	estimatedCredits,
	onClearSelection,
	popoverRef,
	showAddToListPopover,
	onToggleAddToListPopover,
	onAddToList,
	showAddToContacts,
	onAddToContacts,
	isAddingToContacts,
	targetList,
	singleSelect,
	isBulkAdding,
	isWriting,
	onWriteMessage,
	urlFooterStatus,
	confirmMode,
	confirmLabel,
	isConfirming,
	onConfirmSelection,
	footerNote,
}) => {
	const { t } = useTranslation();

	if (foundEntities.totalFound !== 0 && currentTab === Tabs.BY_NAME && totalSelected === 0) {
		return !selectedCompany ? (
			<p className="text-sm text-gray-800">
				Found{" "}
				<span className="font-semibold text-gray-900">
					{`${foundEntities.organizationsFound} companies, `}
					{foundEntities.totalFound} search results, {foundEntities.contactsItems} existing
				</span>
			</p>
		) : (
			<p className="text-sm text-gray-700">
				Filtered to{" "}
				<span className="font-semibold text-gray-900">
					{selectedCompany?.name ? `${selectedCompany.name}` : null}
				</span>
				.{" "}
				<span className="text-sm text-gray-800">
					Press
					<span className="bg-gray-200/50 text-gray-700 text-xs  px-1 py-0.5 mx-0.5"> Esc </span> to
					clear.
				</span>
			</p>
		);
	}

	if (totalSelected > 0) {
		return (
			<div className="flex items-stretch gap-3 w-full">
				<span className="flex items-center gap-2 shrink-0 border border-border-light px-3 rounded-md">
					<span className="text-xs font-medium text-gray-700">{totalSelected} selected</span>
					<button
						className="text-gray-400 hover:text-gray-700 cursor-pointer text-xs leading-none"
						onClick={onClearSelection}
					>
						✕
					</button>
				</span>

				{/* Confirm mode hands the selection to the caller's own mutation, which takes no
				    enrich flags — the toggles would be dead controls there. */}
				{!confirmMode && (
					<>
						<div
							className={`flex flex-row items-center gap-2 w-fit border border-border-light px-3 rounded-md ${isEnrichAllEmails ? "bg-status-success-light border-status-success" : ""}`}
						>
							<Toggle
								className="!w-[28px] !min-w-[28px] !h-[16px] !min-h-[16px] [&>div]:w-3 [&>div]:h-3"
								isChecked={isEnrichAllEmails}
								onChange={() => setIsEnrichAllEmails((prev) => !prev)}
								colorfulToggle="success"
							/>
							<span className="text-sm text-gray-700 leading-tight">Enrich all emails</span>
						</div>

						<div
							className={`flex flex-row items-center gap-2 w-fit border border-border-light px-3 rounded-md ${isEnrichAllPhones ? "bg-status-success-light border-status-success" : ""}`}
						>
							<Toggle
								labelClass="text-text-success!"
								className="!w-[28px] !min-w-[28px] !h-[16px] !min-h-[16px] [&>div]:w-3 [&>div]:h-3"
								isChecked={isEnrichAllPhones}
								onChange={() => setIsEnrichAllPhones((prev) => !prev)}
								colorfulToggle="success"
							/>
							<span className="text-sm text-gray-700 leading-tight">Enrich all phones</span>
						</div>
					</>
				)}

				<div ref={popoverRef} className="relative ml-auto">
					{showAddToListPopover && (
						<div className="absolute bottom-full mb-2 right-0 z-[200]">
							<SelectListPopover
								isLoading={isBulkAdding}
								onCreateNew={(name) => onAddToList({ name })}
								onSelectList={(list) => onAddToList(list)}
							/>
						</div>
					)}
					<div className="flex flex-row w-full items-center gap-2">
						{estimatedCredits.total > 0 && (
							<div className="flex items-center gap-1 border border-gray-300 bg-gray-100 text-status-info text-xs px-2 py-1 rounded-md w-fit shrink-0">
								{estimatedCredits.phone > 0 ? `Max.` : null} {estimatedCredits.total} credits
								<Information
									information={
										<div className="flex flex-col gap-2 text-xs text-gray-700">
											<p className="font-semibold text-gray-900 mb-0.5">Estimated credit cost</p>
											{estimatedCredits.email > 0 && (
												<div className="flex justify-between gap-4">
													<span>Email</span>
													<div className="flex gap-1 items-center">
														<span className="font-medium">
															{`${estimatedCredits.email} × 1 = ${estimatedCredits.email}`}
														</span>
														<Icon className="!w-[10px] !h-[10px] text-gray-700" icon={faCoin} />
													</div>
												</div>
											)}
											{estimatedCredits.phone > 0 && (
												<div className="flex justify-between gap-4">
													<span>Phone</span>
													<div className="flex gap-1 items-center">
														<span className="font-medium">
															up to{" "}
															{`${estimatedCredits.phone / 10} × 10 = ${estimatedCredits.phone}`}
														</span>
														<Icon className="!w-[10px] !h-[10px] text-gray-700" icon={faCoin} />
													</div>
												</div>
											)}
											{estimatedCredits.linkedin > 0 && (
												<div className="flex justify-between gap-4">
													<span>LinkedIn</span>
													<div className="flex gap-1 items-center">
														<span className="font-medium">
															{`${estimatedCredits.linkedin} × 1 = ${estimatedCredits.linkedin}`}
														</span>
														<Icon className="!w-[10px] !h-[10px] text-gray-700" icon={faCoin} />
													</div>
												</div>
											)}
											{estimatedCredits.phone > 0 ? (
												<span className="text-gray-900">
													Phone credits are only charged when a number is actually found, so the
													real cost is usually lower.
												</span>
											) : null}
										</div>
									}
								/>
							</div>
						)}
						{confirmMode ? (
							currentTab === Tabs.BY_URL && urlFooterStatus.hasInvalidChips ? (
								<span className="ml-auto mr-3 text-xs text-status-error">
									{t("companyEnrich.byUrlInvalidPresent")}
								</span>
							) : (
								<Button
									className="ml-auto mr-3"
									title={confirmLabel ?? ""}
									size={ButtonSize.S}
									color={ButtonColor.ACTION}
									isLoading={isConfirming}
									onClick={onConfirmSelection}
								/>
							)
						) : singleSelect ? (
							<Button
								className="ml-auto mr-3"
								title="Write message"
								size={ButtonSize.S}
								color={ButtonColor.ACTION}
								isLoading={isWriting}
								onClick={onWriteMessage}
							/>
						) : currentTab === Tabs.BY_URL && urlFooterStatus.hasInvalidChips ? (
							<span className="ml-auto mr-3 text-xs text-status-error">
								{t("companyEnrich.byUrlInvalidPresent")}
							</span>
						) : (
							<div className="ml-auto mr-3 flex items-center gap-2">
								{showAddToContacts && (
									<Button
										title="Add to contact"
										size={ButtonSize.S}
										color={ButtonColor.ACTION_SECONDARY}
										isLoading={isAddingToContacts}
										onClick={onAddToContacts}
									/>
								)}
								<Button
									title={targetList ? `Add to ${targetList.name}` : "Add to list"}
									size={ButtonSize.S}
									color={ButtonColor.ACTION}
									isLoading={targetList ? isBulkAdding : false}
									onClick={() =>
										targetList ? onAddToList(targetList) : onToggleAddToListPopover()
									}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	if (currentTab === Tabs.BY_FILTERS) {
		return foundEntities.totalFound === 0 ? (
			footerNote ? (
				<p className="text-sm text-gray-600">{footerNote}</p>
			) : (
				<p className="text-sm text-gray-600">
					Set filters on the left, then click{" "}
					<span className="font-semibold text-gray-900">Search</span>
				</p>
			)
		) : (
			<p className="text-sm text-gray-800">
				Found{" "}
				<span className="font-semibold text-gray-900">
					{foundEntities.totalFoundForFilters} search results, {foundEntities.contactsItems}{" "}
					existing
				</span>
			</p>
		);
	}

	if (currentTab === Tabs.BY_URL) {
		if (urlFooterStatus.hasInvalidChips) {
			return <p className="text-sm text-status-error">{t("companyEnrich.byUrlInvalidPresent")}</p>;
		}

		if (urlFooterStatus.nameResultsCount > 0) {
			return (
				<p className="text-sm text-gray-800">
					Found{" "}
					<span className="font-semibold text-gray-900">{urlFooterStatus.nameResultsCount}</span>{" "}
					results
				</p>
			);
		}

		if (urlFooterStatus.resolvedCount > 0) {
			return (
				<p className="text-sm text-gray-800">
					Found <span className="font-semibold text-gray-900">{urlFooterStatus.resolvedCount}</span>{" "}
					LinkedIn profiles
				</p>
			);
		}

		return (
			<p className="text-sm text-gray-500">
				Search LinkedIn by name or paste profile URLs, then add them to a list.
			</p>
		);
	}

	return <span />;
};
